/**
 * Pre-Publish Service
 *
 * Handles pre-publish validation via the story-pre-publish Edge Function.
 *
 * @module prePublishService
 */
import { logger } from '@musedrops/shared';
import { supabase } from './supabase';

interface RunPrePublishParams {
  storyId: string;
  lang?: string; // Language for translation-specific checks
  force?: boolean; // Force retry even if issues unchanged
}

interface PrePublishResult {
  success: boolean;
  status?:
    | 'ready'
    | 'scheduled'
    | 'blocked'
    | 'auto_fixing'
    | 'no_progress'
    | 'skipped'
    | 'reverted';
  message?: string;
  issues?: string[];
  issue?: string;
  error?: string;
}

/**
 * Run pre-publish checks on a story.
 * Returns immediately with the result of the validation.
 */
export const runPrePublishChecks = async (
  params: RunPrePublishParams
): Promise<PrePublishResult> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'story-pre-publish',
      {
        body: {
          storyId: params.storyId,
          ...(params.lang && { lang: params.lang }),
          ...(params.force && { force: params.force }),
        },
      }
    );

    if (error) {
      logger.error('Pre-publish check error', {
        error,
        storyId: params.storyId,
      });
      throw new Error(error.message || 'Failed to run pre-publish checks');
    }

    // Handle the response
    if (data && typeof data === 'object') {
      if (data.success === false) {
        return {
          success: false,
          error: data.error || 'Pre-publish validation failed',
        };
      }

      return {
        success: true,
        status: data.result?.status,
        message: data.result?.message,
        issues: data.result?.issues || data.result?.remainingIssues,
        issue: data.result?.issue,
      };
    }

    return {
      success: false,
      error: 'Unexpected response from pre-publish function',
    };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    logger.error('Pre-publish check failed', {
      error: errorMessage,
      storyId: params.storyId,
    });
    return {
      success: false,
      error: errorMessage,
    };
  }
};
